import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../../services/notificationService';
import Icon from '../AppIcon';
import Button from './Button';

const Header = ({ title = "GrowGuardian Analytics" }) => {
  const { user, signOut } = useAuth();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCounts, setNotificationCounts] = useState({ unread: 0, total: 0 });
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreButtonRef = useRef(null);

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event?.target?.closest('.dropdown-container')) {
        setShowUserMenu(false);
        setShowNotifications(false);
        setShowMoreMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      const [notificationsData, countsData] = await Promise.all([
        notificationService?.getNotifications(user?.id, { limit: 5 }),
        notificationService?.getNotificationCounts(user?.id)
      ]);
      setNotifications(notificationsData || []);
      setNotificationCounts(countsData || { unread: 0, total: 0 });
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      await notificationService?.markAsRead(notification?.id);
      loadNotifications();
      
      // Navigate to relevant page based on notification type
      if (notification?.task_id) {
        navigate('/task-management');
      }
    } catch (error) {
      console.error('Error handling notification:', error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const navigationItems = [
    { label: 'Dashboard', path: '/ipm-operations-overview', icon: 'BarChart3' },
    { label: 'Tasks', path: '/task-management', icon: 'CheckSquare' },
    { label: 'Team', path: '/team-performance-hub', icon: 'Users' },
    { label: 'Analytics', path: '/treatment-analytics', icon: 'TrendingUp' },
    { label: 'Products', path: '/product-browser', icon: 'Package' },
    { label: 'Compliance', path: '/compliance-dashboard', icon: 'FileCheck' },
    { label: 'Calculators', path: '/premium-calculator-suite', icon: 'Calculator' },
    { label: 'Workflows', path: '/automated-workflow-builder', icon: 'GitBranch' },
    { label: 'Learning', path: '/ipm-learning-center', icon: 'BookOpen' },
    { label: 'Audit', path: '/audit-log-dashboard', icon: 'Shield' },
    { label: 'Notifications', path: '/unified-notification-center', icon: 'Bell' },
    { label: 'Client Portal', path: '/client-portal-dashboard', icon: 'Users' }
  ];

  // Split navigation items for responsive design
  const primaryNavItems = navigationItems?.slice(0, 6);
  const moreNavItems = navigationItems?.slice(6);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-3">
          
          {/* Logo and Brand */}
          <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0 min-w-0">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity min-w-0"
            >
              <img 
                src="/assets/images/GrowGuardianGGlogo-1763327971762.png" 
                alt="GrowGuardian logo"
                className="w-7 h-7 sm:w-8 sm:h-8 object-contain flex-shrink-0"
              />
              <div className="hidden sm:block min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">GrowGuardian</h1>
                <p className="text-xs text-gray-600 truncate">AI Agricultural Companion</p>
              </div>
            </button>
          </div>

          {/* Desktop Navigation Tabs - Now visible on laptop screens (lg: 1024px+) */}
          <nav className="hidden lg:flex items-center space-x-1 flex-1 justify-center px-2 max-w-4xl">
            {primaryNavItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => navigate(item?.path)}
                className="flex items-center space-x-1.5 px-2 xl:px-3 py-2 rounded-md text-xs xl:text-sm font-medium text-gray-700 hover:text-green-600 hover:bg-green-50 transition-colors whitespace-nowrap"
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
              </button>
            ))}
            
            {/* More Menu for Additional Items */}
            {moreNavItems?.length > 0 && (
              <div className="relative dropdown-container">
                <button
                  ref={moreButtonRef}
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className="flex items-center space-x-1.5 px-2 xl:px-3 py-2 rounded-md text-xs xl:text-sm font-medium text-gray-700 hover:text-green-600 hover:bg-green-50 transition-colors whitespace-nowrap"
                >
                  <Icon name="MoreHorizontal" size={16} />
                  <span>More</span>
                  <Icon name="ChevronDown" size={14} />
                </button>

                {/* More Dropdown */}
                {showMoreMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                      {moreNavItems?.map((item) => (
                        <button
                          key={item?.path}
                          onClick={() => {
                            navigate(item?.path);
                            setShowMoreMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <Icon name={item?.icon} size={16} />
                          <span>{item?.label}</span>
                        </button>
                      ))}
                      <button
                        onClick={() => {
                          navigate('/predictive-analytics-dashboard');
                          setShowMoreMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <Icon name="TrendingUp" size={16} />
                        <span>Predictive Analytics</span>
                      </button>
                      <button
                        onClick={() => {
                          navigate('/supervisor-alert-management-dashboard');
                          setShowMoreMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <Icon name="Bell" size={16} />
                        <span>Supervisor Alerts</span>
                      </button>
                      <button
                        onClick={() => {
                          navigate('/application-logs');
                          setShowMoreMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <Icon name="FileText" size={16} />
                        <span>Application Logs</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {/* Subscription Badge */}
            {subscription?.status === 'active' && (
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-xs text-gray-600">Active</span>
              </div>
            )}

            {/* User Profile */}
            {user ? (
              <>
                {/* Notifications */}
                <div className="relative dropdown-container flex-shrink-0">
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <Icon name="Bell" size={18} />
                    {notificationCounts?.unread > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-[10px]">
                        {notificationCounts?.unread > 9 ? '9+' : notificationCounts?.unread}
                      </span>
                    )}
                  </button>

                  {/* Notifications Dropdown */}
                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
                          <button
                            onClick={() => setShowNotifications(false)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Icon name="X" size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="max-h-96 overflow-y-auto">
                        {notifications?.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            <Icon name="Bell" size={24} className="mx-auto mb-2 text-gray-400" />
                            <p className="text-sm">No notifications</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-200">
                            {notifications?.map((notification) => (
                              <button
                                key={notification?.id}
                                onClick={() => handleNotificationClick(notification)}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                              >
                                <div className="flex items-start space-x-3">
                                  <div className={`w-2 h-2 rounded-full mt-2 ${
                                    notification?.status === 'pending' ? 'bg-blue-500' : 'bg-gray-300'
                                  }`} />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {notification?.title}
                                    </p>
                                    <p className="text-sm text-gray-600 line-clamp-2">
                                      {notification?.message}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {formatTimeAgo(notification?.created_at)}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {notifications?.length > 0 && (
                        <div className="px-4 py-3 border-t border-gray-200">
                          <button
                            onClick={() => {
                              navigate('/task-management');
                              setShowNotifications(false);
                            }}
                            className="text-sm text-green-600 hover:text-green-700 font-medium"
                          >
                            View all tasks
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* User Menu */}
                <div className="relative dropdown-container flex-shrink-0">
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs sm:text-sm font-medium">
                        {user?.user_metadata?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div className="hidden lg:block text-left min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 max-w-24 xl:max-w-32 truncate">
                        {user?.user_metadata?.full_name || 'User'}
                      </p>
                      <p className="text-[10px] sm:text-xs text-gray-600 max-w-24 xl:max-w-32 truncate">{user?.email}</p>
                    </div>
                    <Icon name="ChevronDown" size={14} className="text-gray-600 hidden lg:block flex-shrink-0" />
                  </button>

                  {/* User Menu Dropdown */}
                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            navigate('/ipm-operations-overview');
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <Icon name="User" size={16} />
                          <span>Profile</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/task-management');
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <Icon name="CheckSquare" size={16} />
                          <span>My Tasks</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/team-performance-hub');
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <Icon name="Users" size={16} />
                          <span>Team</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/user-settings-preferences');
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <Icon name="Settings" size={16} />
                          <span>User Settings</span>
                        </button>
                        <div className="border-t border-gray-100">
                          <button
                            onClick={handleSignOut}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                          >
                            <Icon name="LogOut" size={16} />
                            <span>Sign Out</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="text-gray-600 border-gray-300 hover:bg-gray-50 px-3 py-1.5 text-xs sm:text-sm whitespace-nowrap"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs sm:text-sm whitespace-nowrap"
                >
                  Get Started
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-1.5 sm:p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 flex-shrink-0"
            >
              <Icon name={showMobileMenu ? "X" : "Menu"} size={18} />
            </button>
          </div>
        </div>

        {/* Mobile Menu - Updated breakpoint from xl:hidden to lg:hidden */}
        {showMobileMenu && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-2 py-3 space-y-1 max-h-80 overflow-y-auto">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => {
                    navigate(item?.path);
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                </button>
              ))}
              
              {/* Mobile Quick Actions */}
              <div className="pt-2 border-t border-gray-200 space-y-1">
                <button
                  onClick={() => {
                    navigate('/premium-calculator-suite');
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-green-600 hover:bg-green-50 transition-colors"
                >
                  <Icon name="Calculator" size={18} />
                  <span>Premium Calculators</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/task-management');
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                >
                  <Icon name="CheckSquare" size={18} />
                  <span>Quick Tasks</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;